import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  Timestamp,
} from "firebase/firestore";
import { db } from "./init";
import { convertTimestampToDate } from "./firestore";
import { Actor } from "@/types/actor";
import { buildPublicStorageUrl } from "@/utils/storageUtils";

/**
 * Firestore 演員數據類型（包含 Timestamp）
 */
interface FirestoreActorData {
  name?: string;
  gender?: "male" | "female" | "other";
  birthdate?: Timestamp | Date | null;
  nationality?: string;
  photoURL?: string;
  biography?: string;
  createdAt?: Timestamp | Date | null;
  updatedAt?: Timestamp | Date | null;
  schemaVersion?: number;
}

/**
 * 轉換演員數據（處理 Timestamp）
 */
function convertActorData(data: FirestoreActorData, id: string): Omit<Actor, "id"> {
  return {
    name: data.name || "",
    gender: data.gender,
    birthdate: convertTimestampToDate(data.birthdate) as Date | undefined,
    nationality: data.nationality,
    photoURL: data.photoURL,
    biography: data.biography,
    createdAt: convertTimestampToDate(data.createdAt) as Date | undefined,
    updatedAt: convertTimestampToDate(data.updatedAt) as Date | undefined,
    schemaVersion: data.schemaVersion || 1,
  };
}

/**
 * 獲取所有演員
 */
export async function getActors(): Promise<Actor[]> {
  try {
    const actorsRef = collection(db, "actors");
    let querySnapshot;
    let useClientSort = false;

    try {
      const q = query(actorsRef, orderBy("name", "asc"));
      querySnapshot = await getDocs(q);
    } catch (orderError) {
      console.warn("Failed to order by 'name', using basic query with client-side sort:", orderError);
      querySnapshot = await getDocs(query(actorsRef));
      useClientSort = true;
    }

    const actors = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...convertActorData(doc.data(), doc.id),
    })) as Actor[];

    if (useClientSort) {
      actors.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }

    return actors;
  } catch (error) {
    console.error("Error getting actors:", error);
    throw error;
  }
}

/**
 * 獲取單個演員
 */
export async function getActor(id: string): Promise<Actor | null> {
  try {
    const actorRef = doc(db, "actors", id);
    const actorSnap = await getDoc(actorRef);

    if (actorSnap.exists()) {
      return {
        id: actorSnap.id,
        ...convertActorData(actorSnap.data(), actorSnap.id),
      } as Actor;
    }

    return null;
  } catch (error) {
    console.error("Error getting actor:", error);
    throw error;
  }
}

/**
 * 創建演員
 */
export async function createActor(
  data: Omit<Actor, "id" | "createdAt" | "updatedAt" | "schemaVersion">
): Promise<string> {
  try {
    if (!data.name || data.name.trim().length === 0) {
      throw new Error("演員名稱為必填");
    }

    const actorsRef = collection(db, "actors");
    const actorData: {
      name: string;
      gender?: "male" | "female" | "other";
      birthdate?: Timestamp;
      nationality?: string;
      photoURL?: string;
      biography?: string;
      createdAt: Timestamp;
      updatedAt: Timestamp;
      schemaVersion: number;
    } = {
      name: data.name.trim(),
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      schemaVersion: 1,
    };

    if (data.gender) actorData.gender = data.gender;
    if (data.birthdate) actorData.birthdate = Timestamp.fromDate(data.birthdate);
    if (data.nationality) actorData.nationality = data.nationality;
    if (data.photoURL) actorData.photoURL = data.photoURL;
    if (data.biography) actorData.biography = data.biography;

    const docRef = doc(actorsRef);
    await setDoc(docRef, actorData);
    return docRef.id;
  } catch (error) {
    console.error("Error creating actor:", error);
    throw error;
  }
}

/**
 * 更新演員
 */
export async function updateActor(
  id: string,
  data: Partial<Omit<Actor, "id" | "createdAt" | "schemaVersion">>
): Promise<void> {
  try {
    const actorRef = doc(db, "actors", id);
    const updateData: {
      updatedAt: Timestamp;
      name?: string;
      gender?: "male" | "female" | "other";
      birthdate?: Timestamp;
      nationality?: string;
      photoURL?: string;
      biography?: string;
    } = {
      updatedAt: Timestamp.now(),
    };

    if (data.name !== undefined) updateData.name = data.name.trim();
    if (data.gender !== undefined) updateData.gender = data.gender;
    if (data.birthdate !== undefined) {
      if (data.birthdate) {
        updateData.birthdate = Timestamp.fromDate(data.birthdate);
      }
      // 如果 birthdate 為 null 或 undefined，不設置該欄位（保持原值）
    }
    if (data.nationality !== undefined) {
      updateData.nationality = data.nationality?.trim() || undefined;
    }
    if (data.photoURL !== undefined) {
      updateData.photoURL = data.photoURL || undefined;
    }
    if (data.biography !== undefined) {
      updateData.biography = data.biography?.trim() || undefined;
    }

    await updateDoc(actorRef, updateData);
  } catch (error) {
    console.error("Error updating actor:", error);
    throw error;
  }
}

/**
 * 刪除演員
 */
export async function deleteActor(id: string): Promise<void> {
  try {
    const actorRef = doc(db, "actors", id);
    await deleteDoc(actorRef);
  } catch (error) {
    console.error("Error deleting actor:", error);
    throw error;
  }
}

/**
 * 同步建構演員照片 URL
 *
 * actors bucket 為公開可讀,直接組 CDN URL,瀏覽器自動 disk-cache。
 */
export function getActorPhotoUrl(
  photoUrl: string | undefined,
  defaultPrefix: string = "actor_photos"
): string {
  return buildPublicStorageUrl(photoUrl, defaultPrefix);
}

